<template>
  <div class="user-details">
    <template v-if="isLoaded">
      <template v-if="isFailed">
        <error-message :message="errorMessage | globalize" />
      </template>
      <template v-else>
        <div class="user-details__wrp app__block">
          <h2 class="user-details__title">
            {{ "user-details.title-details" | globalize }}
          </h2>
          <ul class="app-key-value__list">
            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'user-details.lbl-id' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="userDetails.id"
              >
                <copyable :copy-text="userDetails.id.toString()">
                  {{ userDetails.id || '-' }}
                </copyable>
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'user-details.lbl-role' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="userDetails.role"
              >
                {{ userDetails.role | formatUserRole }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'user-details.lbl-nft-role' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="userDetails.id"
              >
                {{ userDetails.nftRole | formatUserRole }}
              </span>
            </li>

            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'user-details.lbl-email' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="userDetails.email"
              >
                <copyable :copy-text="userDetails.email">
                  {{ userDetails.email || '-' }}
                </copyable>
              </span>
            </li>
            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'user-details.lbl-affiliate-id' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="userDetails.affiliateId"
              >
                <copyable :copy-text="userDetails.affiliateId">
                  {{ userDetails.affiliateId || '-' }}
                </copyable>
              </span>
            </li>
            <li class="app-key-value__row">
              <span class="app-key-value__key">
                {{ 'user-details.lbl-affiliate' | globalize }}
              </span>
              <span
                class="app-key-value__value"
                :title="userDetails.affiliate"
              >
                <copyable :copy-text="userDetails.affiliate">
                  {{ userDetails.affiliate || '-' }}
                </copyable>
              </span>
            </li>
            <template v-if="userDetails.affiliate">
              <li class="app-key-value__row">
                <span class="app-key-value__key">
                  {{ 'user-details.lbl-affiliate-locked' | globalize }}
                </span>
                <span
                  class="app-key-value__value"
                  :title="userDetails.affiliateLocked"
                >
                  {{ userDetails.affiliateLocked || '-' }}
                </span>
              </li>
              <li class="app-key-value__row">
                <span class="app-key-value__key">
                  {{ 'user-details.lbl-affiliate-updated-at' | globalize }}
                </span>
                <span
                  class="app-key-value__value"
                  :title="userDetails.affiliateUpdatedAt"
                >
                  {{ userDetails.affiliateUpdatedAt | formatDateDMYT }}
                </span>
              </li>
              <li class="app-key-value__row">
                <span class="app-key-value__key">
                  {{ 'user-details.lbl-affiliate-campaign' | globalize }}
                </span>
                <span
                  class="app-key-value__value"
                  :title="userDetails.affiliateCampaign"
                >
                  {{ userDetails.affiliateCampaign || '-' }}
                </span>
              </li>
            </template>
          </ul>
          <h4 class="details-reader__title app-key-value__key">
            {{ 'user-details.title-addresses' | globalize }}:
          </h4>
          <div
            class="user-details-addresses"
            v-for="chainId in Object.keys(userAddresses)"
            :key="`chainId-${chainId}`"
          >
            <a-list
              v-if="userAddresses[chainId].data.length > 0"
              :data-source="userAddresses[chainId].data"
              item-layout="vertical"
            >
              <template #header>
                <h4>{{ userAddresses[chainId].label }}</h4>
              </template>
              <a-list-item
                slot="renderItem"
                slot-scope="item">
                <a-row>
                  <span class="app-key-value__key">
                    {{ 'user-details.lbl-address' | globalize }}
                    <user-address-type :user-address="item" />
                  </span>
                  <span
                    class="app-key-value__value"
                    :title="item.address"
                  >
                    <copyable
                      class="app-key-value__value"
                      :copy-text="item.address">
                      <a
                        target="_blank"
                        :href="explorerUrl('address', item.address, item.explorerUrl)"
                        :title="item.address">
                        {{ shortener(item.address) }}
                      </a>
                    </copyable>
                  </span>
                </a-row>

                <a-row>
                  <span class="app-key-value__key">
                    {{ 'user-details.lbl-safe-address' | globalize }}
                  </span>

                  <span
                    v-if="item.safeAddresses && item.safeAddresses[chainId]"
                    class="app-key-value__value"
                    :title="item.safeAddresses[chainId]"
                  >
                    <copyable
                      class="app-key-value__value"
                      :copy-text="item.safeAddresses[chainId]"
                    >
                      <a
                        target="_blank"
                        :href="explorerUrl('address',
                                           item.safeAddresses[chainId], item.explorerUrl)"
                        :title="item.safeAddresses[chainId]"
                      >
                        {{ shortener(item.safeAddresses[chainId]) }}
                      </a>
                    </copyable>
                  </span>

                  <span
                    v-else
                    class="app-key-value__value"
                  >
                    --
                  </span>
                </a-row>

                <a-row>
                  <span class="app-key-value__key">
                    {{ 'user-details.lbl-cpk-address' | globalize }}
                  </span>
                  <span
                    class="app-key-value__value"
                    :title="item.cpkAddress"
                  >
                    <copyable
                      class="app-key-value__value"
                      :copy-text="item.cpkAddress">
                      <a
                        target="_blank"
                        :href="explorerUrl('address', item.cpkAddress, item.explorerUrl)"
                        :title="item.cpkAddress"
                      >
                        {{ shortener(item.cpkAddress) }}
                      </a>
                    </copyable>
                  </span>
                </a-row>
              </a-list-item>
            </a-list>
          </div>

          <h4 class="details-reader__title app-key-value__key">
            {{ 'user-details.title-deactivated-addresses' | globalize }}:
          </h4>
          <div
            class="user-details-addresses"
            v-for="chainId in Object.keys(deactivatedAddresses)"
            :key="`deactivatedAddresses-chainId-${chainId}`"
          >
            <a-list
              v-if="deactivatedAddresses[chainId].data.length > 0"
              :data-source="deactivatedAddresses[chainId].data"
              item-layout="vertical"
            >
              <template #header>
                <h4>{{ deactivatedAddresses[chainId].label }}</h4>
              </template>
              <a-list-item
                slot="renderItem"
                slot-scope="item">
                <a-row>
                  <span class="app-key-value__key">
                    {{ 'user-details.lbl-address' | globalize }}
                    <user-address-type :user-address="item" />
                  </span>
                  <span
                    class="app-key-value__value"
                    :title="item.address"
                  >
                    <copyable
                      class="app-key-value__value"
                      :copy-text="item.address">
                      <a
                        target="_blank"
                        :href="explorerUrl('address', item.address, item.explorerUrl)"
                        :title="item.address">
                        {{ shortener(item.address) }}
                      </a>
                    </copyable>
                  </span>
                </a-row>
              </a-list-item>
            </a-list>
          </div>

          <div class="user-details__actions app__form-actions">
            <template v-if="!isChangeRoleMode">
              <div class="user-details__actions-wrp">
                <permission-wrapper
                  skip-alert
                  page-name="user"
                  resource-name="user"
                  action-name="update_foreign">
                  <button
                    v-ripple
                    class="user-details__actions-btn app__button-raised"
                    @click="isChangeRoleMode = true"
                    :disabled="formMixin.isDisabled"
                  >
                    {{ 'user-details.btn-change-role' | globalize }}
                  </button>
                </permission-wrapper>
                <permission-wrapper
                  v-if="cddVerificationExists"
                  skip-alert
                  page-name="verifications"
                  resource-name="verification"
                  action-name="update_foreign">
                  <button
                    v-ripple
                    class="
                      user-details__actions-btn
                      app__button-raised
                      app__button-raised--success
                    "
                    @click="redirectToVerificationDetails(verificationId)"
                    :disabled="formMixin.isDisabled"
                  >
                    {{ 'user-details.btn-continue-cdd' | globalize }}
                  </button>
                </permission-wrapper>
                <permission-wrapper
                  skip-alert
                  v-else
                  page-name="verifications"
                  resource-name="verification"
                  action-name="create">
                  <button
                    v-ripple
                    class="
                      user-details__actions-btn
                      app__button-raised
                      app__button-raised--success
                    "
                    @click="startCDDVerification"
                    :disabled="formMixin.isDisabled"
                  >
                    {{ 'user-details.btn-start-cdd' | globalize }}
                  </button>
                </permission-wrapper>
              </div>
            </template>
            <template v-else>
              <div class="user-details__change-role-block">
                <select-field
                  class="user-details__change-role-selection"
                  :label="'admin-manager-form.lbl-account-role' | globalize"
                  v-model="form.role"
                  :disabled="isPending"
                  @blur="touchField('form.role')"
                  :error-message="getFieldErrorMessage('form.role')"
                >
                  <option
                    v-for="item in roles"
                    :key="item.id"
                    :value="item.name"
                  >
                    {{ item.name | formatUserRole }}
                  </option>
                </select-field>
                <div class="user-details__change-role-actions">
                  <form-confirmation
                    v-if="formMixin.isConfirmationShown"
                    :is-pending="isPending"
                    @ok="updateUserRole"
                    @cancel="hideConfirmation"
                  />
                  <template v-else>
                    <button
                      v-ripple
                      class="user-details__actions-btn app__button-raised"
                      @click="showConfirmation()"
                    >
                      {{ 'user-details.btn-save' | globalize }}
                    </button>
                    <button
                      v-ripple
                      class="
                          user-details__actions-btn
                          app__button-raised
                          app__button-raised--danger"
                      @click="isChangeRoleMode = false"
                    >
                      {{ 'user-details.btn-cancel' | globalize }}
                    </button>
                  </template>
                </div>
              </div>
            </template>

            <template v-if="!isChangeRoleMode">
              <div class="user-details__actions-wrp">
                <form-confirmation
                  v-if="formMixin.isConfirmationShown"
                  :is-pending="isPending"
                  @ok="updateUserRole"
                  @cancel="hideConfirmation"
                />
                <template v-else>
                  <permission-wrapper
                    skip-alert
                    page-name="user"
                    resource-name="user"
                    action-name="update_foreign">
                    <button
                      v-ripple
                      class="
                        user-details__actions-btn
                        app__button-raised
                        app__button-raised--warning"
                      @click="form.role = USER_ROLES.suspend; showConfirmation()"
                      :disabled="formMixin.isDisabled"
                    >
                      {{ 'user-details.btn-suspend' | globalize }}
                    </button>
                    <button
                      v-ripple
                      class="
                        user-details__actions-btn
                        app__button-raised
                        app__button-raised--danger"
                      @click="form.role = USER_ROLES.reject; showConfirmation()"
                      :disabled="formMixin.isDisabled"
                    >
                      {{ 'user-details.btn-reject' | globalize }}
                    </button>
                  </permission-wrapper>
                </template>
              </div>
            </template>
          </div>
          <div class="user-details__actions-wrp user-details__sof-status">
            <div>SoF status: {{ resolvedSofStatus }}</div>
            <permission-wrapper
              v-if="showVerifyButton"
              skip-alert
              page-name="verifications"
              resource-name="verification"
              action-name="update">
              <button
                v-ripple
                class="user-details__actions-btn app__button-raised"
                @click="verifySofStatus"
                :disabled="formMixin.isDisabled"
              >
                {{ 'user-details.btn-verify' | globalize }}
              </button>
            </permission-wrapper>
          </div>

          <div class="user-details__section">
            <h3 class="user-details__subtitle">
              {{ "user-details.title-general" | globalize }}
            </h3>
            <details-reader
              :details="userDetails.general"
              :formatters="formatters" />
          </div>

          <div class="user-details__section">
            <h3 class="user-details__subtitle">
              {{ "user-details.title-address" | globalize }}
            </h3>
            <details-reader :details="userDetails.address" />
          </div>

          <div class="user-details__section">
            <h3 class="user-details__subtitle">
              {{ "user-details.title-documents" | globalize }}
            </h3>
            <details-reader :details="userDetails.documentsInfo" />
          </div>

          <user-details-photos :user-details="userDetails" />

          <div class="user-details__section">
            <h3 class="user-details__subtitle">
              {{ "user-details.title-other" | globalize }}
            </h3>
            <details-reader :details="userDetails.other" />
          </div>

          <user-details-anchors :user-details="userDetails" />
        </div>
      </template>
    </template>
    <template v-else>
      <loader
        message-id="loading"
        position-center
      />
    </template>
  </div>
</template>

<script>
import AList from 'ant-design-vue/lib/list'
import ARow from 'ant-design-vue/lib/row'
import DetailsReader from '@/vue/common/DetailsReader/DetailsReader'
import UserDetailsPhotos from './components/UserDetailsPhotos'
import UserDetailsAnchors from './components/UserDetailsAnchors'
import UserAddressType from './components/UserAddressType'
import Loader from '@/vue/common/Loader'
import ErrorMessage from '@/vue/common/ErrorMessage'
import Copyable from '@/vue/common/copyable/Copyable'
import { api } from '@/api'
import { vueRoutes } from '@/vue-router/routes'
import { vuexTypes } from '@/vuex'
import { mapGetters } from 'vuex'
import { UserRecord } from '@/js/records/entities/user.record'
import { VerificationCDDFormer } from '@/js/formers/VerificationCDDFormer'
import { ErrorHandler } from '@/js/helpers/error-handler'
import { clearObject } from '@/js/helpers/clear-object'
import FormMixin from '@/vue/mixins/form.mixin'
import PermissionMixin from '@/vue/mixins/permission.mixin'
import { Bus } from '@/js/helpers/event-bus'
import { formatDate } from '@/js/utils/date.util'
import { explorerUrl } from '@/js/helpers/urls'
import { shortener } from '@/js/helpers/string'
import PermissionWrapper from '@/vue/common/permission-wrapper'
import { REQUEST_STATUS_RESOLVER, REQUEST_STATUSES } from '@/js/enums'

const REQUEST_TYPES = {
  changeRole: 'change_user_role',
  createVerification: 'create_verification',
}

const USER_ROLES = {
  reject: 'tier99',
  suspend: 'tier90',
}

export default {
  name: 'user-details',
  components: {
    DetailsReader,
    Loader,
    ErrorMessage,
    UserDetailsPhotos,
    UserDetailsAnchors,
    UserAddressType,
    'copyable': Copyable,
    'a-list': AList,
    'a-list-item': AList.Item,
    ARow,
    PermissionWrapper,
  },
  mixins: [FormMixin, PermissionMixin],
  props: {
    id: { type: [Number, String], required: true },
  },

  data () {
    return {
      isLoaded: false,
      isFailed: false,
      isPending: false,
      userDetails: {},
      userAddresses: {},
      deactivatedAddresses: {},
      errorMessage: 'user-details.default-error-message',
      form: {
        role: '',
      },
      roles: [],
      verificationId: null,
      isChangeRoleMode: false,
      cddVerificationExists: false,
      USER_ROLES,
      formatters: [
        {
          key: 'birthDate',
          formatter: (value) => {
            const date = formatDate(value)
            return date
          },
        },
      ],
      sofStatus: REQUEST_STATUSES.not_started,
    }
  },

  computed: {
    ...mapGetters([
      vuexTypes.accountAddress,
      vuexTypes.accountId,
    ]),
    resolvedSofStatus () {
      return REQUEST_STATUS_RESOLVER[this.sofStatus]
    },
    showVerifyButton () {
      return [REQUEST_STATUSES.not_started, REQUEST_STATUSES.rejected].includes(this.sofStatus)
    },
  },

  async created () {
    await this.initUserDetails()
    this.form.role = this.userDetails.role
  },

  methods: {
    async initUserDetails () {
      this.isLoaded = false

      await this.getUserDetails()
      await this.getUsersRoles()
      await this.getSoFStatus()
      const can = this.isForbidden('verifications', 'verification', 'read_foreign')
      if (!can) {
        await this.getVerificationByUser()
      }
      if (!this.isForbidden('users', 'address', 'read_foreign')) {
        await this.getUserAddresses()
      }
      if (!this.isForbidden('users', 'user', 'read_foreign')) {
        await this.getUserDeactivatedAddresses()
      }

      this.isLoaded = true
    },

    async getUserDetails () {
      try {
        let { data } = await api.getWithSignature(`/users/${this.id}`)
        this.userDetails = new UserRecord(data)
      } catch (e) {
        this.isFailed = true
        this.errorMessage = 'user-details.not-found-error-message'
        ErrorHandler.processWithoutFeedback(e)
      }

      try {
        let { data } = await api.getWithSignature(`/userdata/${this.id}`)
        this.userDetails = new UserRecord({
          ...this.userDetails,
          ...data,
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
        await this.getUserDataFromVerification()
      }
    },

    async getUserDataFromVerification () {
      try {
        const query = {
          filter: clearObject({
            user_id: this.id,
            type: 'personal_data',
          }),
        }

        const { data } = await api.getWithSignature('/verifications', query)
        const pd = data.length > 0 ? data[0].data.pd : []
        this.userDetails = new UserRecord({
          ...this.userDetails,
          ...pd,
        })
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getUserAddresses () {
      try {
        const query = {
          filter: clearObject({
            user_id: this.id,
          }),
        }

        const { data } = await api.getWithSignature('/addresses', query)
        // group addresses by chain_id
        const groupedAddresses = {}
        data.forEach(item => {
          if (!groupedAddresses[item.chainId]) {
            groupedAddresses[item.chainId] = { data: [], label: item.chainName }
          }
          groupedAddresses[item.chainId].data.push(item)
        })
        this.userAddresses = groupedAddresses
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getUsersRoles () {
      try {
        const { data } = await api.getWithSignature('/roles')
        this.roles = data
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getVerificationByUser () {
      try {
        const query = {
          filter: clearObject({
            user_id: this.id,
            type: 'manual',
            status: 'pending_manual',
          }),
        }

        const { data } = await api.getWithSignature('/verifications', query)
        if (data.length && data[0].status === 'pending_manual') {
          this.verificationId = data[0].id
          this.cddVerificationExists = true
        }
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async getSoFStatus () {
      try {
        const query = {
          filter: clearObject({
            user_id: this.id,
            type: 'source_of_funds',
          }),
        }

        const { data } = await api.getWithSignature('/verifications', query)

        const sofStatus = data[0] && data[0].status

        if (sofStatus) {
          this.sofStatus = sofStatus
        }
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async verifySofStatus () {
      this.disableForm()

      const userAddress = this.userAddresses[Object.keys(this.userAddresses)[0]].data[0].address

      try {
        let body = JSON.stringify({
          data: {
            type: 'sof_external_verification_form',
            attributes: {
              address: userAddress,
              terms_accepted: false,
              answers_sof: {
                amount: 0,
                source_of_income: '',
                show_other_source: false,
                other_source: '',
                invest_knowledge: '',
                annual_income: '',
                net_worth: '',
                document_url: '',
              },
              answers_suitability: {
                knowledge_stocks: '',
                knowledge_bonds: '',
                school_qualification: '',
                held_positions: '',
                risk_comfortable: '',
                investment_goal: '',
                support_issues: '',
              },
            },
          },
        })

        let { data } = await api.postWithSignature('/sof/verifications/external', body)

        await this.getSoFStatus()

        this.redirectToVerificationDetails(data.id)
        Bus.success('user-details.success-create-message')
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.enableForm()
    },

    async changeUserRole () {
      this.disableForm()

      try {
        let body = JSON.stringify({
          data: {
            type: REQUEST_TYPES.changeRole,
            attributes: { role: this.form.role },
          },
        })

        await api.patchWithSignature(`/users/${this.userDetails.id}/role`, body)
        Bus.success('user-details.success-message')
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.isChangeRoleMode = false
      this.enableForm()
    },

    async startCDDVerification () {
      const former = new VerificationCDDFormer({})
      const defaultFormerOpts = former.buildOps()

      this.disableForm()

      try {
        let body = JSON.stringify({
          data: {
            type: REQUEST_TYPES.createVerification,
            attributes: {
              user_id: Number.parseInt(this.userDetails.id),
              type: 'manual',
              status: 'pending_manual',
              data: {
                manual: {
                  logs: [{
                    admin_address: this.accountAddress,
                    admin_user_id: this.accountId || 0,
                    action: 'verification_started_manually',
                    date: new Date().toISOString(),
                  }],
                  documentsArray: this.userDetails.documentsArray,
                  ...defaultFormerOpts,
                },
              },
            },
          },
        })

        let { data } = await api.postWithSignature('/verifications', body)
        this.redirectToVerificationDetails(data.id)
        Bus.success('user-details.success-create-message')
      } catch (e) {
        ErrorHandler.processWithoutFeedback(e)
      }

      this.enableForm()
    },

    async getUserDeactivatedAddresses () {
      try {
        let { data } = await api.getWithSignature(`/users/${this.id}/deactivated-addresses`)
        const groupedAddresses = {}
        data.forEach(item => {
          if (!groupedAddresses[item.chainId]) {
            groupedAddresses[item.chainId] = { data: [], label: item.chainName }
          }
          groupedAddresses[item.chainId].data.push(item)
        })

        this.deactivatedAddresses = groupedAddresses
      } catch (e) {
        this.isFailed = true
        this.errorMessage = 'user-details.not-found-error-message'
        ErrorHandler.processWithoutFeedback(e)
      }
    },

    async updateUserRole () {
      this.isPending = true
      await this.changeUserRole()
      this.hideConfirmation()
      await this.getUserDetails()
      this.isPending = false
    },

    redirectToVerificationDetails (id) {
      this.$router.push({
        name: vueRoutes.verificationDetails.name,
        params: { id },
      })
    },
    explorerUrl,
    shortener,
  },
}
</script>

<style lang="scss" scoped>
@import '~@scss/variables';
@import '~ant-design-vue/lib/list/style/index.css';
@import '~ant-design-vue/lib/grid/style/index.css';

.user-details__title {
  margin-bottom: 4rem;
}

.user-details__subtitle {
  margin-bottom: 2rem;
}

.user-details__section {
  margin-top: 3rem;
}

.user-details__actions {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
}

.user-details__actions-wrp {
  display: flex;
  align-items: center;
  gap: 2rem;

  .user-details__actions-btn {
    min-width: 16.5rem;
  }
}

.user-details__change-role-selection {
  min-width: 30.5rem;
}

.user-details__sof-status {
  margin-top: 2rem;
}

.user-details__change-role-block {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-width: 50rem;
  width: 100%;
}

.user-details__change-role-actions {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
}

.user-details-addresses .app-key-value__value {
  float: right;
}

.user-details__details-reader {
  /deep/ .details-reader__wrp-array {
    margin-left: 0;
  }
}

</style>
